import React, { useEffect, useState, useContext } from "react";
import { hot } from "react-hot-loader/root";
import fetch from "unfetch";
import { csvParse, autoType } from "d3-dsv";
import { range } from "d3-array";

import * as S from "./Table.styles.js";
import { BulletedNumber } from "~/styles/index.styles";
import Icon from "~/core/scripts/components/Icon";
import { numberFormat } from "~/core/scripts/utils";
import { TranslationContext } from "~/kits/general/scripts/app.js";

const Table = () => {
  const polyglot = useContext(TranslationContext);
  const [data, setData] = useState([]);
  const [isExpanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch("data/votes-per-pop.csv");
      const csv = await response.text();
      const data = csvParse(csv, autoType)
        .map((d, i) => ({ ...d, ratio: (d.votes / d.pop) * 1000000, key: i }))
        .sort((a, b) => b.ratio - a.ratio)
        .map((d, i) => ({ ...d, rank: i + 1 }));

      setData(data);
    };

    fetchData();
  }, []);

  const renderColumn = (props, i) => (
    <S.Row key={`row-${i}-${props.name}`}>
      <S.Rank>
        <BulletedNumber>
          <span>{props.rank}</span>
        </BulletedNumber>
      </S.Rank>
      <S.StateName>{props.name}</S.StateName>
      <S.Population>
        <S.Value>{numberFormat(props.pop)}</S.Value>{" "}
        <S.Unit>{polyglot.t("inhabitants")}</S.Unit>
      </S.Population>
      <S.Votes>
        <S.Value>{props.votes}</S.Value>{" "}
        <S.Unit>{polyglot.t("electors")}</S.Unit>
      </S.Votes>
      <S.Ratio>
        {numberFormat(props.ratio, 2)}
        {range(Math.ceil(props.ratio)).map((i) => (
          <S.Mask
            key={`mask-${i}`}
            cutOff={i + 1 < props.ratio ? 18 : (props.ratio - i) * 18}
          >
            <Icon key={`elector-${i}`} type="elector" />
          </S.Mask>
        ))}
      </S.Ratio>
    </S.Row>
  );

  return (
    <>
      <S.ExpandLeftAndRight>
        <S.TableHead>
          <S.Cell> </S.Cell>
          <S.Cell>{polyglot.t("state")}</S.Cell>
          <S.Cell hiddenMobile>{polyglot.t("population")}</S.Cell>
          <S.Cell hiddenMobile>{polyglot.t("electors")}</S.Cell>
          <S.Cell>{polyglot.t("electorsPerPop")}</S.Cell>
        </S.TableHead>
        <S.Table>
          {data.filter((_, i) => i < 3).map(renderColumn)}
          {isExpanded ? (
            data
              .filter((_, i, a) => i >= 3 && i < a.length - 3)
              .map(renderColumn)
          ) : (
            <S.ExpandButton onClick={() => setExpanded(true)}>
              {polyglot.t("showMore")} <Icon type="carets-expand" />
            </S.ExpandButton>
          )}
          {data.filter((_, i, a) => i >= a.length - 3).map(renderColumn)}
        </S.Table>
      </S.ExpandLeftAndRight>
      <S.Annotation
        dangerouslySetInnerHTML={{ __html: polyglot.t("dcAnnotation") }}
      />
    </>
  );
};

export default hot(Table);
